import {Course, SchoolClass, SchoolClassCourse} from "models"
import {Image, ImageBackground} from "react-native"
import {memo, shapeComponent, ShapeComponent, Text, useI18n, View} from "shared/base"
import React, {useEffect, useMemo} from "react"
import Actions from "./class-space/actions"
import AppHistory from "shared/history"
import Background from "components/chalky-layout/background.png"
import brainboundLaunch from "./class-space/brainbound-launch"
import CourseBg1Image from "./class-space/course-bg-1.png"
import CourseBg2Image from "./class-space/course-bg-2.png"
import CourseBg3Image from "./class-space/course-bg-3.png"
import CourseBg4Image from "./class-space/course-bg-4.png"
import CourseBg5Image from "./class-space/course-bg-5.png"
import CourseBg6Image from "./class-space/course-bg-6.png"
import Courses from "./class-space/courses"
import CoursesContainer from "./class-space/courses-container"
import Cycler from "shared/cycler"
import {digg} from "diggerize"
import DogBoneFireImage from "./class-space/dog-bone-fire.png"
import DogBoneImage from "./class-space/dog-bone.png"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import HowAreYouDoingModal from "./class-space/how-are-you-doing-modal"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import ImageBackgroundFading from "shared/image-background-fading"
import isUserA from "components/users/is-a"
import Link from "@kaspernj/api-maker/build/link"
import {loading} from "components/layout/loading-blocker"
import Logger from "@kaspernj/api-maker/build/logger"
import LogoImage from "./class-space/logo.png"
import moment from "shared/moment"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import RecommendedCourse from "./class-space/recommended-course"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import TrophyImage from "./class-space/trophy.png"
import UnicornImage from "./class-space/unicorn.png"
import useCollection from "@kaspernj/api-maker/build/use-collection"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useHasPro from "components/subscriptions/use-has-pro"
import useModel from "@kaspernj/api-maker/build/use-model"

const bgImages = [
  CourseBg1Image,
  CourseBg2Image,
  CourseBg3Image,
  CourseBg4Image,
  CourseBg5Image,
  CourseBg6Image
]

/**
 * @typedef {object} BoneButtonInnerProps
 * @property {unknown} image
 * @property {unknown} imageHeight
 * @property {unknown} imageWidth
 * @property {unknown} style
 * @property {unknown} testID
 */

/** @typedef {object} BoneButtonInnerState */

/**
 * @extends {ShapeComponent<BoneButtonInnerProps, BoneButtonInnerState>}
 */
class BoneButtonInner extends ShapeComponent {
  render() {
    const {image, imageHeight, imageWidth} = this.p
    const {style, testID} = this.props

    const boneButtonViewStyle = useMemo(() =>
      Object.assign(
        {
          alignItems: "center",
          justifyContent: "center",
          width: 56,
          height: 56,
          backgroundColor: "#9491ff",
          borderRadius: 24
        },
        style
      )
    , [style])

    const imageStyle = useMemo(() => ({
      height: imageHeight,
      width: imageWidth
    }), [imageHeight, imageWidth])

    return (
      <View style={boneButtonViewStyle} testID={testID}>
        <Image source={image} style={imageStyle} />
      </View>
    )
  }
}

const BoneButton = memo(shapeComponent(BoneButtonInner))

/**
 * @typedef {object} TransparentBoneButtonInnerProps
 * @property {unknown} image
 * @property {unknown} imageHeight
 * @property {unknown} imageWidth
 * @property {unknown} style
 * @property {unknown} testID
 */

/** @typedef {object} TransparentBoneButtonInnerState */

/**
 * @extends {ShapeComponent<TransparentBoneButtonInnerProps, TransparentBoneButtonInnerState>}
 */
class TransparentBoneButtonInner extends ShapeComponent {
  render() {
    const {image, imageHeight, imageWidth} = this.p
    const {style, testID} = this.props

    const viewStyle = useMemo(() =>
      Object.assign(
        {alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: 24},
        style
      )
    , [style])

    return (
      <View style={viewStyle} testID={testID}>
        <Image source={image} style={this.imageStyle ||= {height: imageHeight, width: imageWidth}} />
      </View>
    )
  }
}

const TransparentBoneButton = memo(shapeComponent(TransparentBoneButtonInner))

/**
 * @typedef {object} WhiteArrowBoneButtonInnerProps
 * @property {unknown} image
 * @property {unknown} imageHeight
 * @property {unknown} imageWidth
 * @property {unknown} line1
 * @property {unknown} line2
 * @property {unknown} style
 * @property {unknown} testID
 */

/** @typedef {object} WhiteArrowBoneButtonInnerState */

/**
 * @extends {ShapeComponent<WhiteArrowBoneButtonInnerProps, WhiteArrowBoneButtonInnerState>}
 */
class WhiteArrowBoneButtonInner extends ShapeComponent {
  render() {
    const {line1, line2, image, imageHeight, imageWidth} = this.p
    const {style, testID} = this.props
    const imageStyle = useMemo(() => ({
      height: imageHeight,
      width: imageWidth
    }), [imageHeight, imageWidth])

    const boneButtonViewStyle = useMemo(() =>
      Object.assign(
        {
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          height: 56,
          paddingVertical: 7.5,
          paddingHorizontal: 12,
          backgroundColor: "#fff",
          borderRadius: 24
        },
        style
      )
    , [style])

    return (
      <View style={boneButtonViewStyle} testID={testID}>
        <Image source={image} style={imageStyle} />
        <View>
          <Text style={this.line1TextStyle ||= {color: "#5253ff", fontSize: 17, fontStyle: "italic", fontWeight: 700}}>
            {line1}
          </Text>
          <Text style={this.line2TextStyle ||= {color: "#5253ff", fontSize: 17, fontStyle: "italic", fontWeight: 700}}>
            {line2}
          </Text>
        </View>
      </View>
    )
  }
}

const WhiteArrowBoneButton = memo(shapeComponent(WhiteArrowBoneButtonInner))

/**
 * @typedef {object} RoutesUserSchoolClassesClassSpaceRouteProps
 * @property {unknown} match
 */

/**
 * @typedef {object} RoutesUserSchoolClassesClassSpaceRouteState
 * @property {unknown} activeCourse
 * @property {boolean} brainBreakStartBlocked
 * @property {unknown} brainBreaksThisWeek
 * @property {boolean} challengeStartBlocked
 * @property {unknown} challengesThisWeek
 * @property {boolean} courseStartBlocked
 * @property {unknown} coursesThisWeek
 * @property {boolean} howAreYouDoingModal
 * @property {boolean} showUpgradeOverlay
 * @property {unknown} upgradeOverlayCourseType
 */

/**
 * @extends {ShapeComponent<RoutesUserSchoolClassesClassSpaceRouteProps, RoutesUserSchoolClassesClassSpaceRouteState>}
 */
class RoutesUserSchoolClassesClassSpaceRoute extends ShapeComponent {
  /** @type {RoutesUserSchoolClassesClassSpaceRouteState} */
  state = {
    activeCourse: null,
    brainBreakStartBlocked: false,
    brainBreaksThisWeek: null,
    challengeStartBlocked: false,
    challengesThisWeek: null,
    courseStartBlocked: false,
    coursesThisWeek: null,
    upgradeOverlayCourseType: null,
    showUpgradeOverlay: false,
    howAreYouDoingModal: false
  }

  setup() {
    this.logger ||= new Logger({name: "RoutesUserSchoolClassesClassSpaceRoute"})
    this.logger.setDebug(false)

    setLayout("user")

    const {t} = useI18n({namespace: "js.routes.user.school_classes.class_space"})
    const currentUser = useCurrentUser()
    const {schoolClass, schoolClassId} = useModel(SchoolClass, {
      abilities: {
        SchoolClass: ["brainbound_launch", "random_course", "register_view"]
      },
      match: this.p.match,
      select: {
        SchoolClass: ["classStepId", "deletedAt", "id", "viewedClassSpaceToday"]
      }
    })
    const routeSchoolClass = schoolClass?.id() == schoolClassId ? schoolClass : null
    const canStartRandomCourse = routeSchoolClass ? routeSchoolClass.can("randomCourse") : false
    const canStartBrainboundBattle = routeSchoolClass ? routeSchoolClass.can("brainboundLaunch") : false
    const {hasPro} = useHasPro({schoolClassId, user: currentUser})
    const {courses: recommendedCourses} = useCollection({
      modelClass: Course,
      preloads: ["brain_break", "challenge", "check_in", "course_groups", "visible_course_modules"],
      ransack: {
        for_school_class: schoolClassId,
        highlighted_in_top_true: 1,
        public_true: 1
      },
      select: {
        BrainBreak: ["id"],
        Challenge: ["id"],
        CheckIn: ["id"],
        Course: ["backgroundImageUrl", "coursePageTheme", "description", "estimatedTime", "id", "name"],
        CourseGroup: ["icon", "identifier", "name"],
        CourseModule: ["id", "position"]
      }
    })

    const courseBackgrounds = useMemo(() => {
      if (!recommendedCourses) return

      const courseBackgrounds = {}
      const cycler = new Cycler(bgImages)

      for (const course of recommendedCourses) {
        if (course.hasBackgroundImageUrl()) {
          courseBackgrounds[course.id()] = course.backgroundImageUrl()
        } else {
          courseBackgrounds[course.id()] = cycler.cycle()
        }
      }

      return courseBackgrounds
    }, [recommendedCourses])

    const {schoolClassCourses} = useCollection({
      modelClass: SchoolClassCourse,
      preloads: ["course.brain_break", "course.challenge", "course.check_in", "course.course_groups", "course.visible_course_modules"],
      ransack: {
        school_class_id_eq: schoolClassId,
        state_eq: "started"
      },
      select: {
        BrainBreak: ["id"],
        Challenge: ["id"],
        CheckIn: ["id"],
        Course: ["backgroundImageUrl", "coursePageTheme", "description", "estimatedTime", "id", "name"],
        CourseGroup: ["icon", "identifier", "name"],
        CourseModule: ["id", "position"],
        SchoolClassCourse: ["id"]
      }
    }, [schoolClassId])
    const continueWorkingOnCourses = useMemo(
      () => schoolClassCourses?.map((schoolClassCourse) => ({course: schoolClassCourse.course(), schoolClassCourse})),
      [schoolClassCourses]
    )

    const {courses} = useCollection({
      modelClass: Course,
      preloads: ["brain_break", "challenge", "check_in", "class_steps", "course_groups", "visible_course_modules"],
      ransack: {
        public_true: 1
      },
      select: {
        BrainBreak: ["id"],
        Challenge: ["id"],
        CheckIn: ["id"],
        ClassStep: ["id"],
        Course: ["backgroundImageUrl", "coursePageTheme", "description", "estimatedTime", "id", "name"],
        CourseGroup: ["icon", "identifier", "name"],
        CourseModule: ["id", "position"]
      }
    }, [currentUser?.id()])

    const filteredCourses = useMemo(() => {
      const brainBreakCourses = []
      const challengeCourses = []
      const cultureAndEffortCourses = []
      const virtualEditionsCourses = []

      if (courses) {
        for (const course of courses) {
          let matchingClassStep = false

          if (!routeSchoolClass?.classStepId() || course.classSteps().loaded().length == 0) {
            matchingClassStep = true
          }

          for (const classStep of course.classSteps().loaded()) {
            if (classStep.id() == routeSchoolClass?.classStepId()) {
              matchingClassStep = true
            }
          }

          if (!matchingClassStep) continue

          for (const courseGroup of course.courseGroups().loaded()) {
            if (courseGroup.identifier() == "brain_break") {
              brainBreakCourses.push({course})
            } else if (courseGroup.identifier() == "challenge") {
              challengeCourses.push({course})
            } else if (courseGroup.identifier() == "culture" || courseGroup.identifier() == "effort") {
              cultureAndEffortCourses.push({course})
            } else if (courseGroup.identifier() == "virtual") {
              virtualEditionsCourses.push({course})
            }
          }
        }
      }

      return {brainBreakCourses, challengeCourses, cultureAndEffortCourses, virtualEditionsCourses}
    }, [courses, routeSchoolClass?.id()])

    this.brainBreakCourses = filteredCourses.brainBreakCourses
    this.challengeCourses = filteredCourses.challengeCourses
    this.cultureAndEffortCourses = filteredCourses.cultureAndEffortCourses
    this.virtualEditionsCourses = filteredCourses.virtualEditionsCourses
    this.continueWorkingOnCourses = continueWorkingOnCourses
    this.courseBackgrounds = courseBackgrounds
    this.currentUser = currentUser
    this.hasPro = hasPro
    this.recommendedCourses = recommendedCourses
    this.canStartRandomCourse = canStartRandomCourse
    this.canStartBrainboundBattle = canStartBrainboundBattle
    this.schoolClass = routeSchoolClass
    this.schoolClassId = schoolClassId
    this.t = t


    useMemo(() => {
      if (!this.s.activeCourse && recommendedCourses) {
        setTimeout(() => this.setActiveCourse(recommendedCourses[0]), 0)
      }
    }, [recommendedCourses])

    useEffect(() => {
      if (routeSchoolClass && this.registeredClassViewForSchoolClassId != schoolClassId && routeSchoolClass.can("registerView")) {
        routeSchoolClass.registerView({view: "class_space"})
        this.registeredClassViewForSchoolClassId = schoolClassId

        if (canStartRandomCourse && !routeSchoolClass.viewedClassSpaceToday()) {
          this.setState({howAreYouDoingModal: true})
        }
      }
    }, [routeSchoolClass, canStartRandomCourse, schoolClassId])

    useEffect(() => {
      if (schoolClassId && hasPro === false) {
        this.ensureWeeklyStartsThisWeekLoaded()
      } else {
        this.setState({
          brainBreakStartBlocked: false,
          brainBreaksThisWeek: null,
          challengeStartBlocked: false,
          challengesThisWeek: null,
          courseStartBlocked: false,
          coursesThisWeek: null,
          upgradeOverlayCourseType: null,
          showUpgradeOverlay: false
        })
      }
    }, [hasPro, schoolClassId])
  }

  render() { // eslint-disable-line complexity
    const {continueWorkingOnCourses, courseBackgrounds, recommendedCourses, schoolClass, t} = this.tt
    const {activeCourse} = this.s
    const waitForWeeklyStartsThisWeek = this.tt.hasPro === false && (
      this.s.brainBreaksThisWeek == null ||
      this.s.challengesThisWeek == null ||
      this.s.coursesThisWeek == null
    )
    const startLimitsCacheKey = [
      this.s.brainBreakStartBlocked,
      this.s.challengeStartBlocked,
      this.s.courseStartBlocked,
      this.s.brainBreaksThisWeek,
      this.s.challengesThisWeek,
      this.s.coursesThisWeek
    ].join("-")
    let backgroundImage

    if (activeCourse && courseBackgrounds) {
      backgroundImage = digg(courseBackgrounds, activeCourse.id())
    }

    let upgradeOverlayMainContentIdentifier = "pro/upgrade-overlay/class-space-brain-break-limit-main-content"

    if (this.s.upgradeOverlayCourseType == "challenge") {
      upgradeOverlayMainContentIdentifier = "pro/upgrade-overlay/class-space-challenge-limit-main-content"
    } else if (this.s.upgradeOverlayCourseType == "course") {
      upgradeOverlayMainContentIdentifier = "pro/upgrade-overlay/class-space-course-limit-main-content"
    }

    return (
      <View style={this.rootViewStyle ||= {backgroundColor: "#fff"}} testID="routes/user/school-classes/class-space-route">
        {this.s.howAreYouDoingModal && this.tt.canStartRandomCourse && schoolClass &&
          <HowAreYouDoingModal onRequestClose={this.tt.onHowAreYouDoingModalRequestClose} schoolClass={schoolClass} />
        }
        {this.s.showUpgradeOverlay && schoolClass &&
          <ProUpgradeOverlay
            mainContentIdentifier={upgradeOverlayMainContentIdentifier}
            onRequestClose={this.tt.onUpgradeOverlayRequestClose}
            schoolClass={schoolClass}
          />
        }
        <ImageBackgroundFading
          resizeMode="cover"
          source={backgroundImage || CourseBg1Image}
          style={this.imageBackgroundStyle ||= {height: "100%"}}
          testID="class-space-hero-background"
        >
          <View
            style={this.logoMenuContainerStyle ||= {flexDirection: "row", alignItems: "center", marginVertical: 52, marginHorizontal: 50}}
            testID="class-space-logo-menu-container"
          >
            <Image source={LogoImage} style={this.logoImageStyle ||= {width: 159, height: 60}} testID="class-space-logo" />
            {isUserA(this.tt.currentUser, "tester") &&
              <>
                <View
                  style={this.bonesContainerStyle ||= {
                    flexDirection: "row",
                    marginLeft: "auto",
                    paddingHorizontal: 24,
                    paddingVertical: 14,
                    backgroundColor: "rgba(113, 107, 255, 0.2)",
                    borderColor: "rgba(113, 107, 255, 0.2)",
                    borderRadius: 48,
                    borderWidth: 3
                  }}
                  testID="class-space-bones-container"
                >
                  <BoneButton image={DogBoneImage} imageHeight={40} imageWidth={40} />
                  <BoneButton image={DogBoneFireImage} imageHeight={40} imageWidth={40} style={this.boneButtonMarginStyle ||= {marginLeft: 8}} />
                  <BoneButton image={DogBoneFireImage} imageHeight={40} imageWidth={40} style={this.boneButtonMarginStyle} />
                  <WhiteArrowBoneButton
                    image={DogBoneFireImage}
                    imageHeight={40}
                    imageWidth={40}
                    line1={t(".this_week_line1")}
                    line2={t(".this_week_line2")}
                    style={this.boneButtonMarginStyle}
                  />
                  <TransparentBoneButton image={DogBoneImage} imageHeight={40} imageWidth={40} style={this.boneButtonMarginStyle} />
                </View>
                <View
                  style={this.counterContainerStyle ||= {
                    flexDirection: "row",
                    alignItems: "center",
                    height: 84,
                    marginLeft: 12,
                    paddingHorizontal: 40,
                    paddingVertical: 21,
                    backgroundColor: "rgba(113, 107, 255, 0.2)",
                    borderColor: "rgba(113, 107, 255, 0.2)",
                    borderRadius: 48,
                    borderWidth: 3
                  }}
                  testID="class-space-trophy-counter"
                >
                  <Image source={TrophyImage} style={this.trophyImageStyle ||= {width: 40, height: 40}} testID="class-space-trophy-image" />
                  <Text style={this.trophyTextStyle ||= {marginLeft: 8, color: "#fff", fontSize: 32, fontWeight: 700}}>
                    1208
                  </Text>
                </View>
                <View
                  style={this.unicornContainerStyle ||= {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 158,
                    height: 84,
                    marginLeft: 12,
                    backgroundColor: "rgba(113, 107, 255, 0.2)",
                    borderColor: "rgba(113, 107, 255, 0.2)",
                    borderRadius: 48,
                    borderWidth: 3
                  }}
                  testID="class-space-unicorn-counter"
                >
                  <Image source={UnicornImage} style={this.unicornImageStyle ||= {width: 48, height: 48}} testID="class-space-unicorn-image" />
                  <Text style={this.unicornTextStyle ||= {marginLeft: 12, color: "#fff", fontSize: 32, fontWeight: 700}}>
                    4
                  </Text>
                </View>
              </>
            }
            <Link
              style={this.insightsLinkStyle ||= {marginLeft: isUserA(this.tt.currentUser, "tester") ? 12 : "auto"}}
              testID="class-space-insights-link"
              to={Routes.userSchoolClassInsightsPath(schoolClass?.id())}
            >
              <View
                style={this.insightsContainerStyle ||= {
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 84,
                  height: 84,
                  backgroundColor: "rgba(113, 107, 255, 0.2)",
                  borderColor: "rgba(113, 107, 255, 0.2)",
                  borderRadius: 48,
                  borderWidth: 3
                }}
                testID="class-space-insights-icon-container"
              >
                <Icon color="#fff" name="chart-simple" size={32} />
              </View>
            </Link>
          </View>
          {activeCourse && schoolClass &&
            <View
              style={this.recommendedCourseContainerStyle ||= {marginLeft: 50}}
              testID="class-space-recommended-course-container"
            >
              <RecommendedCourse beforeStartCourse={this.tt.onBeforeStartCourse} course={activeCourse} schoolClass={schoolClass} />
            </View>
          }
          {activeCourse && courseBackgrounds && recommendedCourses &&
            <Courses activeCourse={activeCourse} courseBackgrounds={courseBackgrounds} courses={recommendedCourses} onCourseChange={this.tt.onCourseChange} />
          }
        </ImageBackgroundFading>
        <ImageBackground
          resizeMode="contain"
          source={Background}
          style={this.coursesImageBackgroundStyle ||= {paddingBottom: 50}}
          testID="class-space-courses-background"
        >
          <View
            style={this.actionsContainerStyle ||= {marginTop: 150, marginLeft: 50}}
            testID="class-space-actions-container"
          >
            {schoolClass && this.tt.canStartRandomCourse &&
              <Actions
                onStartBrainBreak={this.tt.onStartBrainBreak}
                onStartBrainboundBattle={this.tt.canStartBrainboundBattle ? this.tt.onStartBrainboundBattle : null}
                onStartChallenge={this.tt.onStartChallenge}
                onStartWoof={this.tt.onStartWoof}
              />
            }
          </View>
          {schoolClass && !waitForWeeklyStartsThisWeek && continueWorkingOnCourses?.length > 0 &&
            <View
              style={this.continueWorkingOnCoursesViewStyle ||= {marginTop: 70}}
              testID="class-space-continue-working-on-courses"
            >
              <CoursesContainer
                courses={continueWorkingOnCourses}
                onPressCourseForCourse={this.tt.onPressCourseForCourse}
                schoolClass={schoolClass}
                startLimitsCacheKey={startLimitsCacheKey}
                title={t(".continue_working_on_dot_dot_dot")}
              />
            </View>
          }
          {schoolClass && !waitForWeeklyStartsThisWeek && this.tt.virtualEditionsCourses?.length > 0 &&
            <View
              style={this.virtualEditionsCoursesViewStyle ||= {marginTop: 70}}
              testID="class-space-virtual-editions-courses"
            >
              <CoursesContainer
                courses={this.tt.virtualEditionsCourses}
                onPressCourseForCourse={this.tt.onPressCourseForCourse}
                schoolClass={schoolClass}
                startLimitsCacheKey={startLimitsCacheKey}
                title={t(".virtual_editions")}
              />
            </View>
          }
          {schoolClass && !waitForWeeklyStartsThisWeek && this.tt.brainBreakCourses?.length > 0 &&
            <View
              style={this.brainBreakCoursesViewStyle ||= {marginTop: 70}}
              testID="class-space-brain-break-courses"
            >
              <CoursesContainer
                courses={this.tt.brainBreakCourses}
                onPressCourseForCourse={this.tt.onPressCourseForCourse}
                schoolClass={schoolClass}
                startLimitsCacheKey={startLimitsCacheKey}
                title={t(".brain_breaks_based_on_your_interests_and_energy")}
              />
            </View>
          }
          {schoolClass && !waitForWeeklyStartsThisWeek && this.tt.challengeCourses?.length > 0 &&
            <View
              style={this.challengeCoursesViewStyle ||= {marginTop: 70}}
              testID="class-space-challenge-courses"
            >
              <CoursesContainer
                courses={this.tt.challengeCourses}
                onPressCourseForCourse={this.tt.onPressCourseForCourse}
                schoolClass={schoolClass}
                startLimitsCacheKey={startLimitsCacheKey}
                title={t(".challenges_based_on_your_grade")}
              />
            </View>
          }
          {schoolClass && !waitForWeeklyStartsThisWeek && this.tt.cultureAndEffortCourses?.length > 0 &&
            <View
              style={this.cultureAndEffortCoursesViewStyle ||= {marginTop: 70}}
              testID="class-space-culture-and-effort-courses"
            >
              <CoursesContainer
                courses={this.tt.cultureAndEffortCourses}
                onPressCourseForCourse={this.tt.onPressCourseForCourse}
                schoolClass={schoolClass}
                startLimitsCacheKey={startLimitsCacheKey}
                title={t(".courses_based_on_your_classroom_mood_landscape")}
              />
            </View>
          }
        </ImageBackground>
      </View>
    )
  }

  onCourseChange = ({course}) => this.setState({activeCourse: course})
  onHowAreYouDoingModalRequestClose = () => this.setState({howAreYouDoingModal: false})
  onUpgradeOverlayRequestClose = () => this.setState({
    showUpgradeOverlay: false,
    upgradeOverlayCourseType: null
  })

  setActiveCourse = (activeCourse) => this.setState({activeCourse})

  loadWeeklyStartsThisWeek = async () => {
    this.debugWeeklyLimits("loadWeeklyStartsThisWeek:start", {
      hasPro: this.tt.hasPro,
      schoolClassId: this.tt.schoolClassId
    })

    const dateFrom = moment().startOf("week").toISOString()
    const dateTo = moment().toISOString()
    const schoolClassCourses = await SchoolClassCourse
      .ransack({
        created_at_gteq: dateFrom,
        created_at_lteq: dateTo,
        school_class_id_eq: this.tt.schoolClassId,
        state_in: ["started", "completed"]
      })
      .preload(["course.brain_break", "course.challenge", "course.check_in", "course.course_groups"])
      .select({
        BrainBreak: ["id"],
        Challenge: ["id"],
        CheckIn: ["id"],
        Course: ["id"],
        CourseGroup: ["identifier"],
        SchoolClassCourse: ["id"]
      })
      .toArray()

    const weeklyStartsThisWeek = {
      brainBreaksThisWeek: 0,
      challengesThisWeek: 0,
      coursesThisWeek: 0
    }

    for (const schoolClassCourse of schoolClassCourses) {
      const courseType = this.courseTypeForCourse({course: schoolClassCourse.course()})

      if (courseType == "brain_break") {
        weeklyStartsThisWeek.brainBreaksThisWeek += 1
      } else if (courseType == "challenge") {
        weeklyStartsThisWeek.challengesThisWeek += 1
      } else if (courseType == "course") {
        weeklyStartsThisWeek.coursesThisWeek += 1
      }
    }

    this.setState({
      brainBreakStartBlocked: this.tt.hasPro === false && weeklyStartsThisWeek.brainBreaksThisWeek >= 1,
      brainBreaksThisWeek: weeklyStartsThisWeek.brainBreaksThisWeek,
      challengeStartBlocked: this.tt.hasPro === false && weeklyStartsThisWeek.challengesThisWeek >= 1,
      challengesThisWeek: weeklyStartsThisWeek.challengesThisWeek,
      courseStartBlocked: this.tt.hasPro === false && weeklyStartsThisWeek.coursesThisWeek >= 1,
      coursesThisWeek: weeklyStartsThisWeek.coursesThisWeek
    })

    this.debugWeeklyLimits("loadWeeklyStartsThisWeek:result", {
      dateFrom,
      dateTo,
      hasPro: this.tt.hasPro,
      weeklyStartsThisWeek
    })

    return weeklyStartsThisWeek
  }

  ensureWeeklyStartsThisWeekLoaded = () => {
    if (this.s.brainBreaksThisWeek != null && this.s.challengesThisWeek != null && this.s.coursesThisWeek != null) {
      return {
        brainBreaksThisWeek: this.s.brainBreaksThisWeek,
        challengesThisWeek: this.s.challengesThisWeek,
        coursesThisWeek: this.s.coursesThisWeek
      }
    }

    if (this.loadingWeeklyStartsThisWeekPromise) {
      return this.loadingWeeklyStartsThisWeekPromise
    }

    this.loadingWeeklyStartsThisWeekPromise = this.loadWeeklyStartsThisWeek().finally(() => {
      this.loadingWeeklyStartsThisWeekPromise = null
    })

    return this.loadingWeeklyStartsThisWeekPromise
  }

  /**
   * Resolves weekly-limit course type from course metadata.
   * @param {{course: Course}} args
   * @returns {"brain_break"|"challenge"|"course"|null}
   */
  courseTypeForCourse = ({course}) => {
    const identifiers = course.courseGroups().loaded().map((courseGroup) => courseGroup.identifier())

    if (identifiers.includes("check_in")) {
      return null
    }

    if (identifiers.includes("challenge")) {
      return "challenge"
    }

    if (identifiers.includes("brain_break")) {
      return "brain_break"
    }

    if (identifiers.length > 0) {
      return "course"
    }

    if (course.checkIn()) {
      return null
    }

    if (course.challenge()) {
      return "challenge"
    }

    if (course.brainBreak()) {
      return "brain_break"
    }

    return "course"
  }

  weeklyStartsThisWeekForCourseType = ({courseType}) => {
    if (courseType == "brain_break") return this.s.brainBreaksThisWeek
    if (courseType == "challenge") return this.s.challengesThisWeek
    if (courseType == "course") return this.s.coursesThisWeek
  }

  openUpgradeOverlay = ({courseType}) => this.setState({
    showUpgradeOverlay: true,
    upgradeOverlayCourseType: courseType
  })

  shouldAllowCourseTypeStart = async ({courseType}) => {
    this.debugWeeklyLimits("shouldAllowCourseTypeStart:entry", {
      brainBreaksThisWeek: this.s.brainBreaksThisWeek,
      challengesThisWeek: this.s.challengesThisWeek,
      courseType,
      coursesThisWeek: this.s.coursesThisWeek,
      hasPro: this.tt.hasPro
    })

    if (!courseType) {
      return true
    }

    if (this.tt.hasPro === true) {
      this.debugWeeklyLimits("shouldAllowCourseTypeStart:allow-pro", {courseType})

      return true
    }

    if (this.tt.hasPro == null) {
      await this.waitForHasProToLoad()

      if (this.tt.hasPro == null) {
        this.debugWeeklyLimits("shouldAllowCourseTypeStart:block-hasPro-not-loaded", {courseType})

        return false
      }
    }

    let weeklyStartsThisWeek = this.weeklyStartsThisWeekForCourseType({courseType})

    if (weeklyStartsThisWeek == null) {
      this.debugWeeklyLimits("shouldAllowCourseTypeStart:load-count", {courseType})
      const loadedWeeklyStartsThisWeek = await this.ensureWeeklyStartsThisWeekLoaded()

      if (courseType == "brain_break") {
        weeklyStartsThisWeek = loadedWeeklyStartsThisWeek.brainBreaksThisWeek
      } else if (courseType == "challenge") {
        weeklyStartsThisWeek = loadedWeeklyStartsThisWeek.challengesThisWeek
      } else if (courseType == "course") {
        weeklyStartsThisWeek = loadedWeeklyStartsThisWeek.coursesThisWeek
      }
    }

    if ((weeklyStartsThisWeek || 0) >= 1) {
      if (courseType == "brain_break") {
        this.setState({brainBreakStartBlocked: true})
      } else if (courseType == "challenge") {
        this.setState({challengeStartBlocked: true})
      } else if (courseType == "course") {
        this.setState({courseStartBlocked: true})
      }

      this.openUpgradeOverlay({courseType})
      this.debugWeeklyLimits("shouldAllowCourseTypeStart:block-weekly-limit", {courseType, weeklyStartsThisWeek})

      return false
    }

    this.debugWeeklyLimits("shouldAllowCourseTypeStart:allow", {courseType, weeklyStartsThisWeek})

    return true
  }

  shouldAllowBrainBreakStart = () => this.shouldAllowCourseTypeStart({courseType: "brain_break"})
  shouldAllowChallengeStart = () => this.shouldAllowCourseTypeStart({courseType: "challenge"})

  onBeforeStartCourse = async ({course}) => {
    const courseType = this.courseTypeForCourse({course})

    if (!(await this.shouldAllowCourseTypeStart({courseType}))) {
      return false
    }

    return true
  }

  onLimitedCoursePress = async ({course, to}) => {
    const courseType = this.courseTypeForCourse({course})

    this.debugWeeklyLimits("onLimitedCoursePress:entry", {
      brainBreaksThisWeek: this.s.brainBreaksThisWeek,
      challengesThisWeek: this.s.challengesThisWeek,
      courseId: course.id(),
      courseType,
      coursesThisWeek: this.s.coursesThisWeek,
      hasPro: this.tt.hasPro,
      to
    })

    if (!(await this.shouldAllowCourseTypeStart({courseType}))) {
      this.debugWeeklyLimits("onLimitedCoursePress:block", {courseId: course.id(), courseType, to})

      return false
    }

    try {
      if (course.visibleCourseModules().loaded().length == 1) {
        this.debugWeeklyLimits("onLimitedCoursePress:take-or-continue", {courseId: course.id(), courseType, to})
        await loading(null, async () => {
          const result = await course.takeOrContinue({school_class_id: this.tt.schoolClassId})

          AppHistory.push(result.url)
        })
      } else {
        this.debugWeeklyLimits("onLimitedCoursePress:navigate", {courseId: course.id(), courseType, to})
        AppHistory.push(to)
      }
    } catch (error) {
      if (this.validationLimitErrorShouldShowUpgradeOverlay({courseType, error})) {
        this.openUpgradeOverlay({courseType})
        this.debugWeeklyLimits("onLimitedCoursePress:show-upgrade-overlay-on-validation-error", {
          courseId: course.id(),
          courseType
        })

        return false
      }

      FlashMessage.errorResponse(error)
    }

    return false
  }

  onPressCourseForCourse = ({course, schoolClassCourse}) => {
    const courseType = this.courseTypeForCourse({course})
    const shouldIntercept = Boolean(courseType)

    this.debugWeeklyLimits("onPressCourseForCourse", {
      brainBreaksThisWeek: this.s.brainBreaksThisWeek,
      challengesThisWeek: this.s.challengesThisWeek,
      courseId: course.id(),
      courseType,
      coursesThisWeek: this.s.coursesThisWeek,
      hasPro: this.tt.hasPro,
      schoolClassCourseId: schoolClassCourse?.id(),
      shouldIntercept
    })

    if (shouldIntercept) {
      return this.tt.onLimitedCoursePress
    }
  }

  /**
   * Returns true when a start-course validation error should open the upgrade overlay.
   * @param {{courseType: "brain_break"|"challenge"|"course"|null, error: Error}} args
   * @returns {boolean}
   */
  validationLimitErrorShouldShowUpgradeOverlay = ({courseType, error}) => {
    if (!courseType) {
      return false
    }

    if (this.tt.hasPro === true) {
      return false
    }

    if (error.apiMakerType != "ValidationError") {
      return false
    }

    if (typeof error.getUnhandledErrors != "function") {
      return false
    }

    const unhandledErrors = error.getUnhandledErrors()
    const baseValidationError = unhandledErrors.find((unhandledError) => unhandledError.getAttributeName() == "base")

    if (!baseValidationError) {
      return false
    }

    return true
  }

  debugWeeklyLimits = (message, data = {}) => {
    this.logger.debug(() => [`[class-space weekly-limits] ${message}`, data])
  }

  waitForHasProToLoad = () => {
    if (this.tt.hasPro != null) {
      return Promise.resolve(this.tt.hasPro)
    }

    if (this.waitForHasProToLoadPromise) {
      return this.waitForHasProToLoadPromise
    }

    const retryUntilHasProLoads = (triesLeft) => {
      if (this.tt.hasPro != null || triesLeft <= 0) {
        return Promise.resolve(this.tt.hasPro)
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve()
        }, 50)
      }).then(() => retryUntilHasProLoads(triesLeft - 1))
    }

    this.waitForHasProToLoadPromise = retryUntilHasProLoads(20).finally(() => {
      this.waitForHasProToLoadPromise = null
    })

    return this.waitForHasProToLoadPromise
  }

  onStartChallenge = async () => {
    if (!this.tt.schoolClass || !this.tt.schoolClass.can("randomCourse")) {
      return
    }

    if (!(await this.shouldAllowChallengeStart())) {
      return
    }

    try {
      await loading(null, async () => {
        const result = await this.tt.schoolClass.randomCourse({type: "challenge"})
        const courseUrl = digg(result, "course_url")

        if (!courseUrl) throw new Error("No course URL given")

        AppHistory.push(courseUrl)
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onStartBrainBreak = async () => {
    if (!this.tt.schoolClass || !this.tt.schoolClass.can("randomCourse")) {
      return
    }

    if (!(await this.shouldAllowBrainBreakStart())) {
      return
    }

    try {
      await loading(null, async () => {
        const result = await this.tt.schoolClass.randomCourse({type: "brain_break"})
        const courseUrl = digg(result, "course_url")

        if (!courseUrl) throw new Error("No course URL given")

        AppHistory.push(courseUrl)
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onStartWoof = async () => {
    if (!this.tt.schoolClass || !this.tt.schoolClass.can("randomCourse")) {
      return
    }

    try {
      await loading(null, async () => {
        const result = await this.tt.schoolClass.randomCourse({type: "check_in"})
        const courseUrl = digg(result, "course_url")

        if (!courseUrl) throw new Error("No course URL given")

        AppHistory.push(courseUrl)
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onStartBrainboundBattle = async () => {
    const {schoolClass, t} = this.tt

    if (!schoolClass || !schoolClass.can("brainboundLaunch")) {
      FlashMessage.error(t(".brainbound_launch_unavailable"))
      throw new Error("Brainbound launch command is not available for this class")
    }

    if (typeof window === "undefined" || !window.location) {
      FlashMessage.error(t(".brainbound_browser_unavailable"))
      throw new Error("Browser location is not available for Brainbound launch")
    }

    try {
      await loading(null, async () => {
        const {url} = await brainboundLaunch(schoolClass)

        if (!url) throw new Error("No Brainbound redirect URL given")

        window.location.href = url
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}

export default memo(shapeComponent(RoutesUserSchoolClassesClassSpaceRoute))
